import Big from 'big.js';
import { format, parseISO, isValid } from 'date-fns';
import {
	CLIENT_DOCUMENT_BLUE,
	CLIENT_DOCUMENT_FONT_FAMILY,
	CLIENT_DOCUMENT_LABEL_COLOR,
	buildClientDocumentFooterText,
	buildClientDocumentPageHeaderText,
	normalizeRowFormat,
	rowFormatClassNames,
	waitForClonedDocumentFonts,
} from '@/utils/clientDocumentTheme';
import { paginateClientDocumentHtml } from '@/utils/clientDocumentPagination';
import {
	DEFAULT_PROPOSAL_PRICING_NOTES,
} from '@/utils/proposalPresets';

export const PROPOSAL_DOCUMENT_TYPE = 'proposal';
export const PROPOSAL_VAT_RATE = 0.2;
export const PROPOSAL_WEEKS_PER_YEAR = 52;
export const PROPOSAL_CURRENCY = 'GBP';

const DEFAULT_COMPANY = {
	name: 'New Order Clean Ltd',
	address: "Office 2, Bennet's House, 21 Leyton Road, Harpenden, AL5 2HU",
	companyNumber: '13205534',
	phone: '07553 702075',
	email: 'info@neworderclean.co.uk',
	website: 'www.neworderclean.co.uk',
};

export const buildProposalFooterText = (company = {}) => buildClientDocumentFooterText(company);

const clone = (value) => JSON.parse(JSON.stringify(value));

const todayIso = () => format(new Date(), 'yyyy-MM-dd');
const currentProposalReferencePrefix = () => `CP/${format(new Date(), 'yyyyMM')}-`;

const safeDate = (value) => {
	if (!value) return null;
	try {
		const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
		return isValid(parsed) ? parsed : null;
	} catch (error) {
		return null;
	}
};

export const formatProposalDate = (value) => {
	const parsed = safeDate(value);
	return parsed ? format(parsed, 'dd MMMM yyyy') : '';
};

const toBigOrNull = (value) => {
	if (value === null || value === undefined || value === '') return null;
	try {
		const big = new Big(String(value).replace(/,/g, '').trim());
		return big;
	} catch (error) {
		return null;
	}
};

export const toNumberOrNull = (value) => {
	const big = toBigOrNull(value);
	if (!big) return null;
	const num = Number(big);
	return Number.isFinite(num) ? num : null;
};

const roundMoney = (value) => {
	const big = value instanceof Big ? value : toBigOrNull(value);
	if (!big) return 0;
	return Number(big.round(2, Big.roundHalfUp).toFixed(2));
};

const moneyBig = (value) => toBigOrNull(value) || new Big(0);

export const formatGbpAmount = (value) => {
	const numeric = toNumberOrNull(value);
	if (numeric === null) return '£0.00';
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numeric);
};

const escapeHtml = (value) => {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

const normalizeText = (value, fallback = '') =>
	value === null || value === undefined ? fallback : String(value);

const normalizeStringArray = (value, fallback = []) =>
	(Array.isArray(value) ? value : fallback).map((item) => normalizeText(item));

const normalizeCompany = (value = {}) => ({
	name: normalizeText(value.name, DEFAULT_COMPANY.name),
	address: normalizeText(value.address, DEFAULT_COMPANY.address),
	companyNumber: normalizeText(value.companyNumber, DEFAULT_COMPANY.companyNumber),
	phone: normalizeText(value.phone, DEFAULT_COMPANY.phone),
	email: normalizeText(value.email, DEFAULT_COMPANY.email),
	website: normalizeText(value.website, DEFAULT_COMPANY.website),
});

const normalizeProperty = (value = {}) => ({
	buildingId: value.buildingId ?? null,
	name: normalizeText(value.name),
	address: normalizeText(value.address),
});

const normalizeServiceArea = (value = {}) => ({
	title: normalizeText(value.title),
	items: normalizeStringArray(value.items),
});

const normalizeTaskSection = (value = {}) => ({
	title: normalizeText(value.title),
	items: normalizeStringArray(value.items),
	footnote: normalizeText(value.footnote),
});

const normalizeScheduleRow = (value = {}) => ({
	label: normalizeText(value.label),
	value: normalizeText(value.value),
	...normalizeRowFormat(value),
	// A row marked as a block is a building priced on its own, at its own hourly rate. A row
	// marked as the total adds up the blocks' hours; a row is one or the other.
	...(value.block === true
		? { block: true, hourlyRate: toNumberOrNull(value.hourlyRate) }
		: value.total === true
			? { total: true }
			: {}),
});

/** "Total Weekly Hours - Akron House" → "Akron House": the part after the last dash. */
export const getScheduleBlockName = (label = '') => {
	const text = normalizeText(label).trim();
	const parts = text.split(/\s[-–—]\s/);
	return (parts.length > 1 ? parts[parts.length - 1] : text).trim();
};

/** "18 hours" → 18, "7,5 hours" → 7.5: the first number in a schedule value. */
export const parseScheduleHours = (value = '') => {
	const match = normalizeText(value).replace(',', '.').match(/\d+(?:\.\d+)?/);
	return match ? Number(match[0]) : 0;
};

/** 24 → "24 hours", 7.5 → "7.5 hours", 1 → "1 hour". */
export const formatScheduleHours = (hours) => {
	const value = Math.round((Number(hours) || 0) * 100) / 100;
	return `${value} ${value === 1 ? 'hour' : 'hours'}`;
};

/** The value of the schedule's total row: the hours of the rows marked as blocks, added up. */
export const getScheduleTotalValue = (rows = []) => {
	const blocks = (Array.isArray(rows) ? rows : []).filter((row) => row?.block === true);
	if (!blocks.length) return '';
	return formatScheduleHours(blocks.reduce((sum, row) => sum + parseScheduleHours(row.value), 0));
};

// Only the first row marked as the total keeps the mark, and its value is always the blocks' sum.
const normalizeScheduleRows = (rows = []) => {
	const normalized = rows.map(normalizeScheduleRow);
	const totalIndex = normalized.findIndex((row) => row.total);
	return normalized.map((row, index) => {
		if (!row.total) return row;
		if (index !== totalIndex) {
			const { total, ...rest } = row;
			return rest;
		}
		return { ...row, value: getScheduleTotalValue(normalized) };
	});
};

/** The buildings priced on their own: schedule rows marked as blocks, with name, hours and rate. */
export const getProposalPricingBlocks = (content = {}) =>
	(Array.isArray(content?.schedule?.rows) ? content.schedule.rows : [])
		.filter((row) => row?.block === true)
		.map((row) => ({
			name: getScheduleBlockName(row.label),
			hoursPerWeek: parseScheduleHours(row.value),
			hourlyRate: toNumberOrNull(row.hourlyRate) ?? 0,
		}));

const scheduleRowClass = (row) => ['schedule-row', ...rowFormatClassNames(row, 'schedule-row')].join(' ');

const normalizeTextSection = (value = {}) => ({
	id: normalizeText(value.id),
	title: normalizeText(value.title),
	paragraphs: normalizeStringArray(value.paragraphs),
});

const normalizeSignatory = (value = {}) => ({
	label: normalizeText(value.label),
	name: normalizeText(value.name),
	role: normalizeText(value.role),
});

const normalizePricing = (value = {}) => ({
	hourlyRate: toNumberOrNull(value.hourlyRate) ?? 19,
	hoursPerVisit: toNumberOrNull(value.hoursPerVisit) ?? 6,
	visitsPerWeekPerBlock: toNumberOrNull(value.visitsPerWeekPerBlock) ?? 2,
	blockCount: toNumberOrNull(value.blockCount) ?? 2,
	weeksPerYear: toNumberOrNull(value.weeksPerYear) ?? PROPOSAL_WEEKS_PER_YEAR,
	vatRate: toNumberOrNull(value.vatRate) ?? PROPOSAL_VAT_RATE,
	notes: normalizeStringArray(value.notes, DEFAULT_PROPOSAL_PRICING_NOTES),
});

export const createProposalTemplateContent = () => ({
	version: 1,
	documentType: PROPOSAL_DOCUMENT_TYPE,
	currency: PROPOSAL_CURRENCY,
	company: { ...DEFAULT_COMPANY },
	footerText: buildProposalFooterText(DEFAULT_COMPANY),
	title: 'CLEANING PROPOSAL',
	subtitle: 'Communal Area Cleaning Services - Residential Block',
	properties: [],
	clientId: null,
	clientName: '',
	reference: currentProposalReferencePrefix(),
	proposalDate: todayIso(),
	preparedBy: 'Zoriana Kneller, Director',
	summaryTitle: 'Proposal Summary',
	summaryParagraphs: [
		'This proposal sets out a regular communal area cleaning service for the residential blocks at Strand and Wedgewood Apartments, covering the shared hallways, staircases, lifts, entrances and associated areas.',
		'We take genuine pride in our work and deliver dependable, high-quality cleaning to recognised professional standards, supported by structured processes, ongoing quality control, and continuous improvement.',
		'Our objective is to keep these shared spaces clean and hygienic, maintained on a reliable schedule, and safe and welcoming for residents and visitors.',
	],
	serviceAreasTitle: 'Service Areas',
	serviceAreas: [],
	taskSections: [],
	scopeClarification: {
		id: 'scope-clarification',
		title: 'Scope Clarification',
		paragraphs: [
			'The quoted price covers the routine cleaning tasks listed in this proposal. Deep cleaning, carpet extraction cleaning and external window cleaning are excluded and can be quoted separately upon request. Any additional services and charges will be agreed in writing before work begins.',
		],
	},
	schedule: {
		title: 'Cleaning Schedule',
		intro: 'Service days are split across the working week so each block is attended twice weekly on alternating days.',
		rows: [
			{ label: 'Frequency', value: '2 visits per week, per block' },
			{ label: 'Service Days - Strand Apartments (J3)', value: 'Monday, Thursday' },
			{ label: 'Service Days - Wedgewood Apartments (J2)', value: 'Tuesday, Friday' },
			{ label: 'Estimated Hours per Visit', value: '6 hours' },
			{ label: 'Time Window', value: 'Daytime cleaning' },
			{ label: 'Total Weekly Hours per Block', value: '12 hours' },
			{ label: 'Total Weekly Hours (both blocks)', value: '24 hours' },
		],
	},
	pricing: normalizePricing(),
	textSections: [
		{
			id: 'invoicing-payment',
			title: 'Invoicing and Payment',
			paragraphs: [
				'Invoices will be issued [monthly in arrears / agreed billing frequency], with payment due within [number] days of the invoice date.',
			],
		},
		{
			id: 'supplies',
			title: 'Supplies and Equipment',
			paragraphs: [
				"Cleaning equipment and materials are provided on-site by the client. Where we are requested to purchase specific products or consumables on the client's behalf, a procurement and handling fee will apply to cover ordering, administration, delivery, stock management, and associated handling costs.",
			],
		},
		{
			id: 'health-safety',
			title: 'Health and Safety',
			paragraphs: [
				'All cleaning activities are carried out in full compliance with current UK health and safety legislation and industry best practice. Relevant H&S documentation, including COSHH assessments, risk assessments, and method statements, is maintained and available upon request.',
			],
		},
		{
			id: 'standards',
			title: 'Standards',
			paragraphs: [
				'Cleaning is delivered to recognised professional standards. New Order Clean Ltd is a Corporate Member of the British Institute of Cleaning Science (BICSc Corporate Membership No. 16015767), and the Director holds a BICSc Licence to Practice together with current external risk assessment and COSHH qualifications.',
			],
		},
		{
			id: 'insurance',
			title: 'Insurance',
			paragraphs: [
				'The Company holds comprehensive insurance cover, with enhanced levels of public liability and employers liability cover appropriate to the residential block environment in which we operate. Evidence of cover is available on request.',
			],
		},
		{
			id: 'quality-control',
			title: 'Quality Control',
			paragraphs: [
				'Quality is maintained through site supervision, routine inspections by management, and clearly defined cleaning standards. Client feedback is actively encouraged, and any issues identified are addressed promptly to ensure consistent service quality and continuous improvement across the site.',
			],
		},
		{
			id: 'proposal-validity',
			title: 'Proposal Validity',
			paragraphs: [
				'This proposal is valid for acceptance for 30 days from the proposal date.',
			],
		},
		{
			id: 'next-steps',
			title: 'Next Steps',
			paragraphs: [
				"On acceptance of this proposal, the agreed services are automatically incorporated into New Order Clean Ltd's Ongoing Cleaning Services Agreement, which governs the day-to-day delivery of the service. The Customer will confirm the proposed commencement date and access arrangements in writing, and any changes to scope, hours, or scheduling will be agreed in writing in advance.",
			],
		},
	],
	acceptance: {
		title: 'Acceptance',
		paragraphs: [
			'Signed acceptance of this proposal confirms agreement to the scope, schedule, and pricing set out above, on the basis described in the Next Steps section.',
		],
		signatories: [
			{
				label: 'Signed for and on behalf of',
				name: 'S3 Maintenance Services Ltd',
				role: '',
			},
			{
				label: 'Signed for and on behalf of',
				name: 'New Order Clean Ltd',
				role: 'Zoriana Kneller\nDirector',
			},
		],
		signatureLabel: 'Signature & Date',
	},
});

export const normalizeProposalContent = (raw = {}) => {
	const template = createProposalTemplateContent();
	const source = raw && typeof raw === 'object' ? raw : {};
	const schedule = source.schedule && typeof source.schedule === 'object' ? source.schedule : {};
	const acceptance = source.acceptance && typeof source.acceptance === 'object' ? source.acceptance : {};
	const company = normalizeCompany(source.company || template.company);

	return {
		version: 1,
		documentType: PROPOSAL_DOCUMENT_TYPE,
		currency: normalizeText(source.currency, PROPOSAL_CURRENCY),
		company,
		footerText: normalizeText(source.footerText, buildProposalFooterText(company)),
		title: normalizeText(source.title, template.title),
		subtitle: normalizeText(source.subtitle, template.subtitle),
		properties: Array.isArray(source.properties)
			? source.properties.map(normalizeProperty)
			: clone(template.properties),
		clientId: source.clientId ?? null,
		clientName: normalizeText(source.clientName, template.clientName),
		reference: normalizeText(source.reference, template.reference),
		proposalDate: normalizeText(source.proposalDate, template.proposalDate || todayIso()),
		preparedBy: normalizeText(source.preparedBy, template.preparedBy),
		summaryTitle: normalizeText(source.summaryTitle, template.summaryTitle),
		summaryParagraphs: normalizeStringArray(source.summaryParagraphs, template.summaryParagraphs),
		serviceAreasTitle: normalizeText(source.serviceAreasTitle, template.serviceAreasTitle),
		serviceAreas: Array.isArray(source.serviceAreas)
			? source.serviceAreas.map(normalizeServiceArea)
			: clone(template.serviceAreas),
		taskSections: Array.isArray(source.taskSections)
			? source.taskSections.map(normalizeTaskSection)
			: clone(template.taskSections),
		// Keep the editor fields available without adding text to existing documents.
		scopeClarification: source.scopeClarification && typeof source.scopeClarification === 'object'
			? normalizeTextSection(source.scopeClarification)
			: { id: 'scope-clarification', title: 'Scope Clarification', paragraphs: [] },
		schedule: {
			title: normalizeText(schedule.title, template.schedule.title),
			intro: normalizeText(schedule.intro, template.schedule.intro),
			rows: Array.isArray(schedule.rows)
				? normalizeScheduleRows(schedule.rows)
				: clone(template.schedule.rows),
		},
		pricing: normalizePricing(source.pricing || template.pricing),
		textSections: Array.isArray(source.textSections)
			? source.textSections.map(normalizeTextSection)
			: clone(template.textSections),
		acceptance: {
			title: normalizeText(acceptance.title, template.acceptance.title),
			paragraphs: normalizeStringArray(acceptance.paragraphs, template.acceptance.paragraphs),
			signatories: Array.isArray(acceptance.signatories)
				? acceptance.signatories.map(normalizeSignatory)
				: clone(template.acceptance.signatories),
			signatureLabel: normalizeText(
				acceptance.signatureLabel,
				template.acceptance.signatureLabel,
			),
		},
	};
};

export const calculateProposalPricing = (pricingInput = {}, blocksInput = []) => {
	const pricing = normalizePricing(pricingInput);
	if (Array.isArray(blocksInput) && blocksInput.length) {
		return calculateBlockPricing(pricing, blocksInput);
	}
	const hourlyRate = moneyBig(pricing.hourlyRate);
	const hoursPerVisit = moneyBig(pricing.hoursPerVisit);
	const visitsPerWeekPerBlock = moneyBig(pricing.visitsPerWeekPerBlock);
	const blockCount = moneyBig(pricing.blockCount);
	const weeksPerYear = moneyBig(pricing.weeksPerYear);
	const vatRate = moneyBig(pricing.vatRate);

	const weeklyCostPerBlock = hourlyRate.times(hoursPerVisit).times(visitsPerWeekPerBlock);
	const weeklyCombined = weeklyCostPerBlock.times(blockCount);
	const annualExVat = weeklyCombined.times(weeksPerYear);
	const vat = annualExVat.times(vatRate);
	const annualIncVat = annualExVat.plus(vat);

	return {
		hourlyRate: roundMoney(hourlyRate),
		hoursPerVisit: Number(hoursPerVisit),
		visitsPerWeekPerBlock: Number(visitsPerWeekPerBlock),
		blockCount: Number(blockCount),
		weeksPerYear: Number(weeksPerYear),
		vatRate: Number(vatRate),
		weeklyCostPerBlock: roundMoney(weeklyCostPerBlock),
		weeklyCombined: roundMoney(weeklyCombined),
		annualExVat: roundMoney(annualExVat),
		vat: roundMoney(vat),
		annualIncVat: roundMoney(annualIncVat),
		usesBlocks: false,
		blocks: [],
	};
};

// Each block costs its hourly rate times its weekly hours; the year and VAT apply to them all.
const calculateBlockPricing = (pricing, blocksInput) => {
	const blocks = blocksInput.map((block) => {
		const hoursPerWeek = moneyBig(block.hoursPerWeek);
		const hourlyRate = moneyBig(block.hourlyRate);
		return {
			name: normalizeText(block.name),
			hoursPerWeek: Number(hoursPerWeek),
			hourlyRate: roundMoney(hourlyRate),
			weeklyCost: roundMoney(hourlyRate.times(hoursPerWeek)),
		};
	});
	const weeksPerYear = moneyBig(pricing.weeksPerYear);
	const vatRate = moneyBig(pricing.vatRate);
	const weeklyCombined = blocks.reduce((sum, block) => sum.plus(block.weeklyCost), new Big(0));
	const hoursPerWeek = blocks.reduce((sum, block) => sum.plus(block.hoursPerWeek), new Big(0));
	const annualExVat = weeklyCombined.times(weeksPerYear);
	const vat = annualExVat.times(vatRate);
	return {
		usesBlocks: true,
		blocks,
		blockCount: blocks.length,
		hoursPerWeek: Number(hoursPerWeek),
		weeksPerYear: Number(weeksPerYear),
		vatRate: Number(vatRate),
		weeklyCombined: roundMoney(weeklyCombined),
		annualExVat: roundMoney(annualExVat),
		vat: roundMoney(vat),
		annualIncVat: roundMoney(annualExVat.plus(vat)),
	};
};

export const getProposalPropertySummary = (contentInput = {}) => {
	const content = normalizeProposalContent(contentInput);
	return content.properties
		.map((property) => [property.name, property.address].filter(Boolean).join(', '))
		.filter(Boolean)
		.join('; ');
};

export const buildProposalPayload = (contentInput = {}, extra = {}) => {
	const content = normalizeProposalContent(contentInput);
	const totals = calculateProposalPricing(content.pricing, getProposalPricingBlocks(content));
	const { metadata = {}, content: _ignoredContent, ...rest } = extra || {};
	return {
		...rest,
		documentType: PROPOSAL_DOCUMENT_TYPE,
		title: content.title || 'Cleaning Proposal',
		reference: content.reference || '',
		documentDate: content.proposalDate || null,
		client: content.clientId || rest.client || null,
		clientName: content.clientName || '',
		propertySummary: getProposalPropertySummary(content),
		totalAmount: totals.annualIncVat,
		content,
		metadata: {
			template: 'cleaning-proposal-v1',
			...metadata,
		},
	};
};

export const deriveProposalFilename = (document = {}) => {
	const content = normalizeProposalContent(document.content || document);
	const reference = content.reference || document.reference || 'proposal';
	const safeReference = String(reference)
		.replace(/[^a-zA-Z0-9._-]+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-+|-+$/g, '');
	return `${safeReference || 'proposal'}.pdf`;
};

const renderParagraphs = (paragraphs = []) =>
	paragraphs
		.filter((paragraph) => String(paragraph || '').trim())
		.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
		.join('');

const renderList = (items = []) => {
	const listItems = items
		.filter((item) => String(item || '').trim())
		.map((item) => `<li>${escapeHtml(item)}</li>`)
		.join('');
	return listItems ? `<ul>${listItems}</ul>` : '';
};

const renderInfoRows = (content) => {
	const propertyHtml = content.properties
		.map((property) => {
			const name = property.name ? `<strong>${escapeHtml(property.name)}</strong>` : '';
			const address = property.address ? `<span>${escapeHtml(property.address)}</span>` : '';
			return `<div class="property-line">${name}${address}</div>`;
		})
		.join('');

	const rows = [
		['Property', propertyHtml],
		['Client', escapeHtml(content.clientName)],
		['Reference', escapeHtml(content.reference)],
		['Proposal Date', escapeHtml(formatProposalDate(content.proposalDate) || content.proposalDate)],
	];

	return rows
		.map(
			([label, value]) => `
				<div class="info-row">
					<div class="info-label">${escapeHtml(label)}</div>
					<div class="info-value">${value || ''}</div>
				</div>
			`,
		)
		.join('');
};

const renderPricingLines = (rows) =>
	rows
		.map(
			([label, value]) => `
				<div class="pricing-row">
					<div>${escapeHtml(label)}</div>
					<div>${escapeHtml(value)}</div>
				</div>
			`,
		)
		.join('');

const formatHours = (value) => String(Number(value) || 0);

// The annual cost, VAT and total stay together: if one moves to the next page, all three do.
const renderPricingSummary = (totals) => `
	<div class="pricing-summary" data-keep-together>
		${renderPricingLines([
			['Estimated Annual Cost (excl. VAT)', formatGbpAmount(totals.annualExVat)],
			[`VAT (${Math.round(totals.vatRate * 100)}%)`, formatGbpAmount(totals.vat)],
			['Estimated Annual Cost (incl. VAT)', formatGbpAmount(totals.annualIncVat)],
		])}
	</div>
`;

// Buildings priced on their own print as a small table, then the year and VAT.
const renderBlockPricing = (totals) => {
	const rows = totals.blocks
		.map(
			(block) => `
				<tr>
					<td>${escapeHtml(block.name)}</td>
					<td class="num">${escapeHtml(formatHours(block.hoursPerWeek))}</td>
					<td class="num">${escapeHtml(formatGbpAmount(block.hourlyRate))}</td>
					<td class="num">${escapeHtml(formatGbpAmount(block.weeklyCost))}</td>
				</tr>
			`,
		)
		.join('');
	return `
		<table class="pricing-blocks">
			<thead>
				<tr>
					<th>Block</th>
					<th class="num">Hours per Week</th>
					<th class="num">Hourly Rate (excl. VAT)</th>
					<th class="num">Weekly Cost (excl. VAT)</th>
				</tr>
			</thead>
			<tbody>
				${rows}
				<tr class="pricing-blocks-total">
					<td>Total</td>
					<td class="num">${escapeHtml(formatHours(totals.hoursPerWeek))}</td>
					<td></td>
					<td class="num">${escapeHtml(formatGbpAmount(totals.weeklyCombined))}</td>
				</tr>
			</tbody>
		</table>
		${renderPricingSummary(totals)}
	`;
};

const renderPricingRows = (content) => {
	const totals = calculateProposalPricing(content.pricing, getProposalPricingBlocks(content));
	if (totals.usesBlocks) return renderBlockPricing(totals);
	const hourlyRate = `${formatGbpAmount(totals.hourlyRate)} per hour`;
	const weeklyRows = totals.blockCount === 1
		? [['Weekly Cost (excl. VAT)', formatGbpAmount(totals.weeklyCombined)]]
		: [
			['Weekly Cost per Block (excl. VAT)', formatGbpAmount(totals.weeklyCostPerBlock)],
			['Weekly Cost - Blocks Combined (excl. VAT)', formatGbpAmount(totals.weeklyCombined)],
		];
	return renderPricingLines([['Hourly Rate (excl. VAT)', hourlyRate], ...weeklyRows]) + renderPricingSummary(totals);
};

const renderFooterText = (value) => escapeHtml(value).replace(/\r?\n/g, '<br>');

const estimateTextUnits = (value = '', charsPerLine = 86) => {
	const text = String(value || '').trim();
	if (!text) return 0;
	return text
		.split(/\r?\n/)
		.reduce((sum, line) => sum + Math.max(1, Math.ceil(String(line || '').length / charsPerLine)), 0);
};

const estimateParagraphUnits = (paragraphs = []) =>
	paragraphs
		.filter((paragraph) => String(paragraph || '').trim())
		.reduce((sum, paragraph) => sum + estimateTextUnits(paragraph, 92) + 1, 0);

const estimateListUnits = (items = []) =>
	items
		.filter((item) => String(item || '').trim())
		.reduce((sum, item) => sum + Math.max(1, estimateTextUnits(item, 78)), 1);

const paginateProposalBlocks = (blocks = []) => {
	const pages = [];
	let current = [];
	let usedUnits = 0;
	let capacity = 59;

	blocks.forEach((block) => {
		const blockUnits = Math.max(1, block.units || 1);
		if (current.length && (block.forceNewPage || usedUnits + blockUnits > capacity)) {
			pages.push(current);
			current = [];
			usedUnits = 0;
			capacity = 69;
		}
		current.push(block);
		usedUnits += blockUnits;
	});

	if (current.length) {
		pages.push(current);
	}

	return pages.length ? pages : [[]];
};

const renderProposalPage = ({ blocks = [], pageNumber = 1, footerText = '' }) => {
	const blocksHtml = blocks
		.map((block) => {
			if (block.kind !== 'service-area') return block.html;
			const heading = block.serviceAreaIndex === 0
				? `<h2>${escapeHtml(block.serviceAreasTitle)}</h2>`
				: '';
			return `
				<section class="proposal-section">
					${heading}
					${block.html}
				</section>`;
		})
		.join('');

	return `
			<article class="pdf-gen-invoice-container proposal-document" data-proposal-page="${pageNumber}">
				${blocksHtml}
				<footer class="proposal-page-footer">${footerText}</footer>
			</article>`;
};

export const generateProposalHtml = ({ content: contentInput, logoUrl = '/logo-m.svg' } = {}) => {
	const content = normalizeProposalContent(contentInput);
	const footerText = renderFooterText(content.footerText);
	const pricingRows = renderPricingRows(content);

	const scheduleRows = content.schedule.rows
		.map(
			(row) => `
				<div class="${scheduleRowClass(row)}">
					<div>${escapeHtml(row.label)}</div>
					<div>${escapeHtml(row.value)}</div>
				</div>
			`,
		)
		.join('');

	const signatories = content.acceptance.signatories
		.map(
			(signatory) => `
				<div class="signature-box">
					<p>${escapeHtml(signatory.label)}</p>
					<strong>${escapeHtml(signatory.name)}</strong>
					${signatory.role ? `<span>${escapeHtml(signatory.role).replace(/\n/g, '<br>')}</span>` : '<span>&nbsp;</span>'}
					<div class="signature-line"></div>
					<small>${escapeHtml(content.acceptance.signatureLabel)}</small>
				</div>
			`,
		)
		.join('');

	const blocks = [
		{
			key: 'cover',
			units: 24,
			html: `
				<div class="proposal-cover-block">
				<header class="proposal-topbar">
					<div></div>
					${logoUrl ? `<img class="proposal-logo" src="${escapeHtml(logoUrl)}" alt="">` : ''}
				</header>

				<h1 class="proposal-title">${escapeHtml(content.title)}</h1>
				<p class="proposal-subtitle">${escapeHtml(content.subtitle)}</p>

				<div class="info-table">
					${renderInfoRows(content)}
				</div>
				</div>`,
		},
		{
			key: 'summary',
			units: 5 + estimateParagraphUnits(content.summaryParagraphs),
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(content.summaryTitle)}</h2>
					${renderParagraphs(content.summaryParagraphs)}
				</section>`,
		},
		...content.serviceAreas.map((area, index) => ({
			key: `service-area-${index}`,
			kind: 'service-area',
			serviceAreaIndex: index,
			serviceAreasTitle: content.serviceAreasTitle,
			units: 6 + estimateListUnits(area.items),
			html: `
				<div class="subsection">
					<h3>${escapeHtml(area.title)}</h3>
					${renderList(area.items)}
				</div>`,
		})),
		...content.taskSections.map((section, index) => ({
			key: `task-${index}`,
			units: 6 + estimateListUnits(section.items) + (section.footnote ? 2 : 0),
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(section.title)}</h2>
					${renderList(section.items)}
					${section.footnote ? `<p class="footnote">${escapeHtml(section.footnote)}</p>` : ''}
				</section>`,
		})),
		...(content.scopeClarification.paragraphs.some((paragraph) => paragraph.trim()) ? [{
			key: 'scope-clarification',
			units: 5 + estimateParagraphUnits(content.scopeClarification.paragraphs),
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(content.scopeClarification.title)}</h2>
					${renderParagraphs(content.scopeClarification.paragraphs)}
				</section>`,
		}] : []),
		{
			key: 'schedule',
			units: 8 + estimateTextUnits(content.schedule.intro, 92) + content.schedule.rows.length * 2,
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(content.schedule.title)}</h2>
					${content.schedule.intro ? `<p>${escapeHtml(content.schedule.intro)}</p>` : ''}
					<div class="schedule-table">${scheduleRows}</div>
				</section>`,
		},
		{
			key: 'pricing',
			units: 18 + getProposalPricingBlocks(content).length * 2 + estimateParagraphUnits(content.pricing.notes),
			html: `
				<section class="proposal-section">
					<h2>Pricing</h2>
					<div class="pricing-table">${pricingRows}</div>
					<div class="pricing-notes">${renderParagraphs(content.pricing.notes)}</div>
				</section>`,
		},
		...content.textSections.map((section, index) => ({
			key: `text-${section.id || index}`,
			units: 5 + estimateParagraphUnits(section.paragraphs),
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(section.title)}</h2>
					${renderParagraphs(section.paragraphs)}
				</section>`,
		})),
		{
			key: 'acceptance',
			units:
				11 +
				estimateParagraphUnits(content.acceptance.paragraphs) +
				content.acceptance.signatories.length * 7,
			html: `
				<section class="proposal-section">
					<h2>${escapeHtml(content.acceptance.title)}</h2>
					${renderParagraphs(content.acceptance.paragraphs)}
					<div class="signatures">${signatories}</div>
				</section>`,
		},
	];

	const pages = paginateProposalBlocks(blocks);
	const pagesHtml = pages
		.map((pageBlocks, index) =>
			renderProposalPage({
				blocks: pageBlocks,
				pageNumber: index + 1,
				footerText,
			}),
		)
		.join('');

	return `<!doctype html>
<html>
	<head>
		<meta charset="UTF-8">
		<title>${escapeHtml(content.title)} ${escapeHtml(content.reference)}</title>
		<style>
			.pdf-gen-invoice-document {
				background: #f5f5f5;
				color: #333;
				font-family: ${CLIENT_DOCUMENT_FONT_FAMILY};
				margin: 0;
				padding: 0;
			}
			.pdf-gen-invoice-container.proposal-document {
				width: 8.27in;
				height: 11.68in;
				margin: 0 auto;
				padding: 0.3in 0.74in 1.02in;
				background: #fff;
				font-size: 12px;
				font-weight: 400;
				line-height: 1.38;
				box-sizing: border-box;
				position: relative;
				overflow: hidden;
				page-break-after: always;
				break-after: page;
			}
			.pdf-gen-invoice-container.proposal-document:last-child {
				page-break-after: auto;
				break-after: auto;
			}
			.proposal-page-footer {
				position: absolute;
				left: 0.74in;
				right: 0.74in;
				bottom: 0.32in;
				padding-top: 10px;
				border-top: 1px solid #d9d9d9;
				color: #333;
				font-size: 10px;
				line-height: 1.25;
				white-space: pre-wrap;
			}
			.proposal-page-number {
				display: block;
				margin-top: 4px;
				color: #777;
				font-size: 9px;
				text-align: right;
			}
			/* From page 2: the document's name above a thin line, as on the director's Word documents. */
			.proposal-page-header {
				margin: 0 0 22px;
				padding-bottom: 5px;
				border-bottom: 1px solid #d9d9d9;
				color: #333;
				font-size: 10px;
				line-height: 1.25;
				text-align: right;
			}
			.proposal-topbar {
				display: flex;
				justify-content: space-between;
				align-items: flex-start;
				min-height: 82px;
			}
			.proposal-logo {
				width: auto;
				max-width: 96px;
				max-height: 75px;
				height: auto;
				object-fit: contain;
			}
			.proposal-title {
				margin: 0 0 6px;
				color: ${CLIENT_DOCUMENT_BLUE};
				font-size: 21px;
				font-weight: 700;
				letter-spacing: 0;
			}
			.proposal-subtitle {
				margin: 0 0 28px;
				font-size: 14px;
				font-weight: 600;
			}
			.info-table {
				border-top: 0;
				border-bottom: 0;
				margin-bottom: 20px;
			}
			.info-row,
			.schedule-row,
			.pricing-row {
				display: grid;
				grid-template-columns: 200px 1fr;
				gap: 14px;
				align-items: start;
				border-top: 1px solid #dedede;
				padding: 7px 0 8px;
			}
			/* Schedule and pricing labels run longer than their values, so these rows split in half. */
			.schedule-row,
			.pricing-row {
				grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
			}
			.schedule-row > div:last-child {
				text-align: right;
			}
			.info-row:first-child,
			.schedule-row:first-child,
			.pricing-row:first-child {
				border-top: none;
			}
			.info-label,
			.info-row strong {
				color: ${CLIENT_DOCUMENT_LABEL_COLOR};
				font-weight: 600;
			}
			.info-value {
				display: flex;
				flex-direction: column;
				gap: 5px;
			}
			.property-line {
				display: flex;
				flex-direction: column;
				gap: 2px;
			}
			.proposal-cover-block {
				display: flow-root;
			}
			.proposal-section {
				display: flow-root;
				break-inside: avoid;
				page-break-inside: avoid;
				margin: 0 0 22px;
			}
			.proposal-section h2 {
				color: ${CLIENT_DOCUMENT_BLUE};
				font-size: 16px;
				font-weight: 600;
				margin: 0 0 10px;
			}
			.subsection {
				margin-bottom: 11px;
			}
			.subsection h3 {
				color: ${CLIENT_DOCUMENT_BLUE};
				font-size: 14px;
				font-weight: 600;
				margin: 0 0 6px;
			}
			p {
				margin: 0 0 9px;
			}
			ul {
				margin: 0 0 8px 18px;
				padding: 0;
			}
			li {
				margin: 0 0 4px;
				padding-left: 2px;
			}
			.schedule-table,
			.pricing-table {
				border-top: 0;
				border-bottom: 0;
			}
			.schedule-row > div:first-child,
			.pricing-row > div:first-child {
				color: ${CLIENT_DOCUMENT_LABEL_COLOR};
				font-weight: 500;
			}
			.schedule-row.schedule-row--bold > div {
				font-weight: 700;
			}
			.schedule-row.schedule-row--italic > div {
				font-style: italic;
			}
			.schedule-row.schedule-row--blue > div {
				color: ${CLIENT_DOCUMENT_BLUE};
			}
			.pricing-row div:last-child {
				font-weight: 600;
			}
			.pricing-row > div:last-child {
				text-align: right;
			}
			.pricing-summary {
				border-top: 1px solid #dedede;
			}
			.pricing-blocks {
				width: 100%;
				border-collapse: collapse;
				font-variant-numeric: tabular-nums;
			}
			.pricing-blocks th,
			.pricing-blocks td {
				padding: 7px 10px 8px 0;
				text-align: left;
				vertical-align: top;
			}
			.pricing-blocks th {
				color: ${CLIENT_DOCUMENT_LABEL_COLOR};
				font-weight: 500;
				border-bottom: 1px solid #dedede;
			}
			.pricing-blocks td {
				border-top: 1px solid #dedede;
			}
			.pricing-blocks tbody tr:first-child td {
				border-top: none;
			}
			.pricing-blocks .num {
				text-align: right;
			}
			.pricing-blocks th:last-child,
			.pricing-blocks td:last-child {
				padding-right: 0;
			}
			.pricing-blocks .pricing-blocks-total td {
				font-weight: 600;
			}
			.signature-box strong {
				font-weight: 600;
			}
			.pricing-notes {
				margin: 12px 0 0;
				padding: 0;
				font-style: italic;
			}
			.pricing-notes p {
				margin: 0 0 8px;
			}
			.pricing-notes p:last-child {
				margin-bottom: 0;
			}
			.footnote {
				font-size: 11px;
				font-style: italic;
			}
			.signatures {
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 44px;
				margin-top: 22px;
			}
			.signature-box {
				min-height: 150px;
				display: flex;
				flex-direction: column;
				gap: 8px;
			}
			.signature-box p,
			.signature-box strong,
			.signature-box span {
				margin: 0;
			}
			.signature-line {
				border-bottom: 1px solid #333;
				margin-top: auto;
				height: 34px;
			}
			@media print {
				body {
					margin: 0;
				}
				.pdf-gen-invoice-container.proposal-document {
					box-shadow: none;
					margin: 0;
				}
			}
		</style>
	</head>
	<body class="print-preview-root">
		<div class="pdf-gen-invoice-document" data-client-document-autopaginate="proposal">
			<template data-proposal-footer-template>${footerText}</template>
			<template data-proposal-header-template>${escapeHtml(buildClientDocumentPageHeaderText(content.reference, content.title))}</template>
${pagesHtml}
		</div>
	</body>
</html>`;
};

const waitForImages = async (root) => {
	const images = root ? Array.from(root.querySelectorAll('img')) : [];
	if (!images.length) return;
	await Promise.all(
		images.map(
			(img) =>
				new Promise((resolve) => {
					if (img.complete) {
						resolve();
						return;
					}
					const done = () => {
						img.onload = null;
						img.onerror = null;
						resolve();
					};
					img.onload = done;
					img.onerror = done;
					setTimeout(done, 1000);
				}),
		),
	);
};

const downloadPdfBlob = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};

const compressPdfBytes = async (pdfBytes) => {
	const { PDFDocument } = await import('pdf-lib');
	const input = pdfBytes instanceof ArrayBuffer ? new Uint8Array(pdfBytes) : pdfBytes;
	const pdfDoc = await PDFDocument.load(input, { ignoreEncryption: true });
	return pdfDoc.save({
		useObjectStreams: true,
		updateFieldAppearances: false,
		subsetFonts: true,
	});
};

const generateProposalPdfBytesFromHtml = async ({ html, filename, logError } = {}) => {
	const { default: html2pdf } = await import('html2pdf.js').catch(() => ({ default: null }));
	if (!html2pdf) {
		throw new Error('PDF generator not available. Please install html2pdf.js');
	}
	const container = document.createElement('div');
	container.style.position = 'absolute';
	container.style.left = '-9999px';
	container.innerHTML = html;
	// Wide registers (company records) are laid out on landscape pages.
	const orientation = container.querySelector('[data-pdf-orientation="landscape"]') ? 'landscape' : 'portrait';
	container.style.width = orientation === 'landscape' ? '11.69in' : '8.27in';
	document.body.appendChild(container);
	try {
		await waitForImages(container);
		await paginateClientDocumentHtml(container);
		const el =
			container.querySelector('.pdf-gen-invoice-document') ||
			container.querySelector('.pdf-gen-invoice-container') ||
			container;
		await waitForImages(el);
		const exportScale = Math.max(2.5, window.devicePixelRatio || 1);
		const worker = html2pdf().from(el).set({
			margin: 0,
			filename,
			image: { type: 'jpeg', quality: 0.88 },
			html2canvas: { scale: exportScale, useCORS: true, onclone: waitForClonedDocumentFonts },
			jsPDF: { unit: 'in', format: 'a4', orientation, compress: true },
			pagebreak: { mode: ['css', 'legacy'] },
		});
		const pdfArrayBuffer = await worker.outputPdf('arraybuffer');
		if (!pdfArrayBuffer) {
			throw new Error('PDF generation failed');
		}

		let pdfBytes = pdfArrayBuffer;
		try {
			pdfBytes = await compressPdfBytes(pdfArrayBuffer);
		} catch (error) {
			logError?.(error, 'compressProposalPdfBytes');
		}

		return pdfBytes;
	} finally {
		document.body.removeChild(container);
	}
};

export const generateClientDocumentPdfFileFromHtml = async ({ html, filename, logError } = {}) => {
	const safeFilename = String(filename || 'document.pdf').trim() || 'document.pdf';
	const pdfBytes = await generateProposalPdfBytesFromHtml({
		html,
		filename: safeFilename,
		logError,
	});
	return new File([pdfBytes], safeFilename, {
		type: 'application/pdf',
		lastModified: Date.now(),
	});
};

export const downloadProposalPdfFromHtml = async ({ html, filename, logError } = {}) => {
	const file = await generateClientDocumentPdfFileFromHtml({ html, filename, logError });
	downloadPdfBlob(file, file.name);
};
